import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BookOpen, Loader2, Package, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import type { Brand, BrandInput } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { brandsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { useCan } from '@/features/auth/store';

type Draft = BrandInput & { slug: string };
type BrandRow = Brand & { productCount: number; leadCount: number };
const NONE = '__none__';
const KB_STATUS: Record<
  string,
  { label: string; variant: 'secondary' | 'outline' | 'destructive' }
> = {
  complete: { label: 'pronta', variant: 'secondary' },
  in_progress: { label: 'indexando', variant: 'outline' },
  error: { label: 'erro', variant: 'destructive' },
};

function KbCell({
  kb,
  fallback,
}: {
  kb: { name: string; status: string; sources: number } | undefined;
  fallback: string;
}) {
  const st = kb ? KB_STATUS[kb.status] : null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <BookOpen className="size-3.5" />
      {kb?.name ?? fallback}
      {st && <Badge variant={st.variant}>{st.label}</Badge>}
      {kb && !kb.sources && <Badge variant="outline">sem fontes</Badge>}
    </span>
  );
}

export function BrandsPage() {
  const qc = useQueryClient();
  const canEdit = useCan('products.manage');
  const q = useQuery({ queryKey: ['brands'], queryFn: brandsApi.list });
  const kbs = useQuery({
    queryKey: ['brands', 'knowledge-bases'],
    queryFn: brandsApi.knowledgeBases,
    // Base recém-criada fica "indexando" por alguns segundos.
    refetchInterval: (query) =>
      query.state.data?.items.some((k) => k.status === 'in_progress') ? 5_000 : false,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const kbById = new Map((kbs.data?.items ?? []).map((k) => [k.id, k]));

  const createKb = useMutation({
    mutationFn: (slug: string) => brandsApi.createKnowledgeBase(slug),
    onSuccess: (brand) => {
      toast.success(`Base "${brand.name}" criada no Retell — adicione as fontes lá`);
      setDraft((d) => (d ? { ...d, knowledgeBaseId: brand.knowledgeBaseId } : d));
      void qc.invalidateQueries({ queryKey: ['brands'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao criar a base'),
  });

  const save = useMutation({
    mutationFn: ({ slug, ...body }: Draft) =>
      brandsApi.update(slug, {
        ...body,
        description: body.description || null,
        knowledgeBaseId: body.knowledgeBaseId || null,
      }),
    onSuccess: () => {
      toast.success('Marca salva — o agente da marca se atualiza na próxima ligação');
      void qc.invalidateQueries({ queryKey: ['brands'] });
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['settings', 'agents'] });
      setDraft(null);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar'),
  });

  const semDescricao = (q.data ?? []).filter((b) => !b.description).length;
  const produtosDaMarca = q.data?.find((b) => b.slug === draft?.slug)?.productCount ?? 0;

  return (
    <div>
      <PageHeader
        title="Marcas"
        description="O que vale para todas as variações de um produto — a IA usa isto quando o produto não tem descrição própria"
      />

      {semDescricao > 0 && (
        <Alert className="mb-4">
          <AlertTriangle />
          <AlertDescription>
            {semDescricao} marca(s) sem descrição. Nas ligações desses produtos, a IA é instruída a
            dizer que vai confirmar com a equipe em vez de responder — o que costuma encerrar a
            conversa antes da oferta.
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-lg border bg-card">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : q.data?.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Marca</TableHead>
                <TableHead>Origem</TableHead>
                <TableHead className="text-center">Produtos</TableHead>
                <TableHead className="text-center">Leads</TableHead>
                <TableHead>O que a IA sabe</TableHead>
                <TableHead>Base de conhecimento</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.data.map((b: BrandRow) => (
                <TableRow key={b.slug}>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell className="font-mono text-xs">{b.slug}</TableCell>
                  <TableCell className="text-center tabular-nums">{b.productCount}</TableCell>
                  <TableCell className="text-center tabular-nums">{b.leadCount}</TableCell>
                  <TableCell className="max-w-md">
                    {b.description ? (
                      <span className="line-clamp-2 text-sm text-muted-foreground">
                        {b.description}
                      </span>
                    ) : (
                      <Badge variant="outline">pendente</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {b.knowledgeBaseId ? (
                      <KbCell kb={kbById.get(b.knowledgeBaseId)} fallback={b.knowledgeBaseId} />
                    ) : (
                      <span className="text-muted-foreground">— (agente genérico)</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-8"
                        onClick={() =>
                          setDraft({
                            slug: b.slug,
                            name: b.name,
                            description: b.description,
                            knowledgeBaseId: b.knowledgeBaseId,
                          })
                        }
                      >
                        <Pencil />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Package}
              title="Nenhuma marca"
              description="Marcas nascem junto com os produtos, a partir da origem (source_slug) dos leads importados."
            />
          </div>
        )}
      </div>

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        {/* A descrição pode ser longa: o diálogo rola dentro da tela, e o rodapé fica acessível. */}
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar marca</DialogTitle>
            <DialogDescription>
              A descrição vale para os {produtosDaMarca} produto(s) desta marca, inclusive os que
              forem criados depois pela importação.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(draft);
              }}
            >
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input
                  value={draft.name ?? ''}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Só para identificar aqui no painel. O que a IA fala é o nome de cada produto.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>O que a IA sabe sobre o produto</Label>
                <Textarea
                  rows={10}
                  className="max-h-72 overflow-y-auto"
                  value={draft.description ?? ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="O que é, ingredientes, como usar, quanto dura, quando faz efeito, contraindicações, garantia, prazo de entrega."
                />
                <p className="text-xs text-muted-foreground">
                  Vai inteiro para o prompt de toda ligação desta marca, e é a única fonte que a IA
                  pode usar para tirar dúvida — ela é instruída a não inventar nada além disto.{' '}
                  <strong>Não inclua preço, desconto nem quantidade de frascos</strong>: isso varia
                  por produto e já é enviado separadamente. Um produto específico pode ter descrição
                  própria, que substitui esta.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Base de conhecimento (Retell)</Label>
                <div className="flex gap-2">
                  <Select
                    value={draft.knowledgeBaseId ?? NONE}
                    onValueChange={(v) =>
                      setDraft({ ...draft, knowledgeBaseId: v === NONE ? null : v })
                    }
                    disabled={!!kbs.data?.error}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Nenhuma — usa o agente genérico</SelectItem>
                      {(kbs.data?.items ?? []).map((k) => (
                        <SelectItem key={k.id} value={k.id}>
                          {k.name} · {KB_STATUS[k.status]?.label ?? k.status} · {k.sources} fonte(s)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!draft.knowledgeBaseId && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => createKb.mutate(draft.slug)}
                      disabled={createKb.isPending || !!kbs.data?.error}
                      title="Cria a base no Retell com a descrição desta marca como primeira fonte e vincula"
                    >
                      {createKb.isPending ? <Loader2 className="animate-spin" /> : <BookOpen />}{' '}
                      Criar base
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {kbs.data?.error
                    ? `Bases indisponíveis: ${kbs.data.error}`
                    : 'Marca com base ganha um agente próprio no Retell, só com essa base — nenhuma outra marca enxerga o conteúdo. "Criar base" usa a descrição acima como primeira fonte; as demais (páginas, arquivos, textos) você adiciona no painel do Retell. Sem preço nas fontes: o script ignora valores vindos da base.'}
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDraft(null)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={save.isPending || !draft.name}>
                  {save.isPending && <Loader2 className="animate-spin" />} Salvar
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
